<template>
  <v-card
    id="create_group_root"
    class="pa-4"
  >
    <v-card-title>
      {{ !updateMode ? $t('userGroups.createOrAdd.create.title') : $t('userGroups.createOrAdd.add.title') }}
    </v-card-title>

    <v-text-field
      id="group_name"
      v-model="groupName"
      :class="{ 'shake-animation': groupNameInvalidAnimation }"
      :label="$t('userGroups.createOrAdd.create.nameTextfield')"
      :counter="maxCharacters"
      :rules="[rules.required]"
    ></v-text-field>
    <v-text-field
      id="search_users_field"
      v-model="search"
      :label="$t('dataTable.search')"
      append-icon="mdi-magnify"
      single-line
      hide-details
      clearable
      class="mb-2"
    ></v-text-field>
    <v-data-table
      id="virtual-scroll-table"
      v-model="selected"
      :headers="headers"
      :options.sync="pagination"
      item-key="id"
      show-select
      :search="search"
      class="elevation-1"
      :class="{ 'shake-animation': userIdsInvalidAnimation }"
      :server-items-length="totalItems"
      :items="usersList"
      :footer-props="{
        'items-per-page-text': $t('dataTable.footer.itemsPerPageText'),
        'page-text': $t('dataTable.footer.pageText'),
        'items-per-page-options': [5,10,15],
      }"
    >
    </v-data-table>
    <v-card-actions>
      <v-btn @click="$emit('finished')">{{ $t('main.cancel') }}</v-btn>
      <v-btn
        id="create_group"
        color="primary"
        :loading="isLoading"
        @click="createOrUpdateGroup"
      >
        {{ !!updateMode ? $t('main.update') : $t('main.create') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import debounce from 'lodash.debounce'
import { showToastError, showToastSuccess } from '../../../event-bus'
import usergroups from '../../../services/api/userGroups'
import users from '../../../services/api/users'
import businessAccount from '@/services/api/businessAccount'
export default {
  props: {
    baId: {
      type: String,
      default: undefined
    },
    userGroupId: {
      type: Number,
      default: undefined
    },
    updateMode: {
      type: Boolean,
      default: false
    }
  },
  emits: ['finished'],
  data() {
    return {
      groupName: '',
      groupNameInvalidAnimation: false,
      userIdsInvalidAnimation: false,
      maxCharacters: 255,
      rules: {
        required: (v) => !!v || !isNaN(parseFloat(v)) || this.$t('validation.form.requiredField'),
        minMax: (v) => (v && v.length <= this.maxCharacters && v.length >= this.minCharacters) || this.$t('validation.form.mustBeBetween', { min: this.minCharacters, max: this.maxCharacters })
      },

      search: '',
      usersList: [],
      selected: [],
      headers: [
        {
          text: this.$t('dataTable.header.users.id'),
          align: 'left',
          value: 'id',
          class: 'text-uppercase'
        },
        {
          text: this.$t('dataTable.header.users.name'),
          align: 'left',
          value: 'firstName',
          class: 'text-uppercase'
        },

        {
          text: this.$t('dataTable.header.users.email'),
          align: 'left',
          value: 'email',
          class: 'text-uppercase'
        }

      ],
      usersTableLoading: false,
      pagination: {
        sortBy: ['name'],
        itemsPerPage: 5
      },
      totalItems: 0,
      isLoading: false
    }
  },

  watch: {
    search() {
      this.dataTableLoading = true
      this.listUsersWatcher()
    },
    pagination: {
      handler() {
        this.listUsers()
      }
    }

  },

  async mounted() {
    this.listUsers()
    if (this.updateMode) {
      try {
        const group = await usergroups.getUserGroup(this.userGroupId)
        this.groupName = group.name
        this.selected = group.members
      } catch (error) {
        console.error(error)
      }
    }
  },
  methods: {
    async listUsers() {
      try {
        this.usersTableLoading = true
        const pageRequest = {
          'page-num': this.pagination.page,
          'page-size': this.pagination.itemsPerPage || 5,
          'sort-field': this.pagination.sortBy[0],
          'sort-order': this.pagination.sortDesc[0] ? 'desc' : 'asc'
        }
        const page = await businessAccount.listMembers(this.baId, this.search, pageRequest)
        // console.log('page', page)
        this.usersList = page.content
        // console.log('this.usersList', this.usersList)
        this.totalItems = page.total
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      } finally {
        this.dataTableLoading = false
      }
    },
    listUsersWatcher: debounce(function() {
      this.listUsers()
    }, 500),

    async createOrUpdateGroup(e) {
      e.preventDefault()

      if (!this.groupName.trim().length) {
        this.groupNameInvalidAnimation = true
        setTimeout(() => (this.groupNameInvalidAnimation = false), 500)
        return
      }

      if (this.selected.length === 0) {
        this.userIdsInvalidAnimation = true
        setTimeout(() => (this.userIdsInvalidAnimation = false), 500)
        return
      }

      this.isLoading = false
      const selectedIds = this.selected.map(x => x.id)
      // console.log('selectedIds', selectedIds)

      try {
        if (!this.updateMode) {
          const req = {
            businessAccountId: this.baId,
            name: this.groupName,
            userIds: selectedIds
          }
          await usergroups.createUserGroup(req)
          showToastSuccess(this.$t('userGroups.succesfullyCreated'))
        } else {
          const req = {
            businessAccountId: this.baId,
            name: this.groupName,
            userIds: selectedIds
          }
          await usergroups.updateUserGroup(this.userGroupId, req)
          showToastSuccess(this.$t('userGroups.succesfullyUpdated'))
        }

        this.$emit('finished')
      } catch (error) {
        showToastError(this.getErrorMessage(error))
        console.error(error)
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style scoped>
#virtual-scroll-table {
  max-height: 50vh;
  overflow-y: auto;
  overflow-x: hidden !important;
}
</style>
